import React, { useContext, useEffect, useMemo, useState } from "react";
import { AccountContext } from 'components/Account/Account';
import ContainerRangosFolios from 'components/Container/ContainerRangosFolios';
import empresas from 'api/empresas';
import getDevicesByTenant from 'api/getDevicesByTenant';
import getRangoFolios from 'api/getRangoFolios';
import downloadCSV from '../csv/downloadCSV';

const columns = [
  {
    name: "Rut",
    selector: (row) => row.rut,
    sortable: true,
    width: "10%",
  },
  {
    name: "Device ID",
    selector: (row) => row.device_id,
    sortable: true,
    width: "35%",
  },
  {
    name: "Tipo de Documento",
    selector: (row) => row.document_type,
    sortable: true,
    width: "15%",
  },
  {
    name: "Folio Inicial",
    selector: (row) => row.folio_ini,
    sortable: true,
    width: "14%",
  },
  {
    name: "Folio Final",
    selector: (row) => row.folio_fin,
    sortable: true,
    width: "14%",
  },
  {
    name: "Fecha petición",
    selector: (row) => new Date(row.fecha_peticion).toISOString().slice(0, 10),
    sortable: true,
    width: "12%",
  },
];

function RangosFolios() {
  const { getSession } = useContext(AccountContext);
  const [user, setUser] = useState(null);

  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [tipoDocumento, setTipoDocumento] = useState(39);

  const [empresaList, setEmpresaList] = useState([]);
  const [empresa, setEmpresa] = useState("");
  const [companyLoading, setCompanyLoading] = useState(true);

  const [device, setDevice] = useState('');
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);

  const [folioIni, setFolioIni] = useState('');
  const [folioFin, setFolioFin] = useState('');

  const [responseData, setResponseData] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    getSession().then((session) => {
      setUser(session);
      empresas.getEmpresas(session.idToken.jwtToken)
        .then((response) => {
          setEmpresaList(response.data);
          setCompanyLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setCompanyLoading(false);
        });
    });
  }, []);

  const empresaOptions = useMemo(() =>
    empresaList.map((e) => ({
      value: e.tenant_id,
      label: `${e.rut} - ${e.name}`,
    })),
    [empresaList]
  );

  useEffect(() => {
    async function fetchDevices() {
      if (!empresa || !user) {
        setDeviceOptions([]);
        setDevice('');
        return;
      }
      setDeviceLoading(true);
      setDeviceOptions([]);
      try {
        const response = await getDevicesByTenant(user.idToken.jwtToken, empresa);
        const arrayOptions = [{ value: '', label: 'Todos' }];
        (response?.data?.records || []).forEach((element) => {
          arrayOptions.push({ value: element.device_id, label: element.device_id });
        });
        setDeviceOptions(arrayOptions);
      } catch (error) {
        console.log(error);
      } finally {
        setDeviceLoading(false);
      }
    }
    fetchDevices();
  }, [empresa, user]);

  const handleSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSearchError('');

    if (empresa === "") {
      setSearchError("Debe seleccionar una empresa");
      return;
    }
    if (folioIni && folioFin && parseInt(folioIni) > parseInt(folioFin)) {
      setSearchError("El folio inicial debe ser menor al folio final");
      return;
    }

    try {
      setSearching(true);
      const params = {
        tenant_id: empresa,
        tipodocumento: tipoDocumento,
        startDate,
        endDate,
        device_id: device,
        folio_ini: folioIni,
        folio_fin: folioFin,
      };
      const response = await getRangoFolios(params, user.idToken.jwtToken);
      setResponseData(response.data || []);
    } catch (error) {
      console.log(error);
      setSearchError("Error al buscar folios");
    } finally {
      setSearching(false);
    }
  };

  const actionsMemo = useMemo(() => ({
    onExport: () => {
      try {
        const timestamp = new Date().toISOString().replace(/:/g, "-");
        const filename = `export_RangoFolios_${timestamp}.csv`;
        downloadCSV(responseData || [], filename);
      } catch (error) {
        console.log(error);
      }
    }
  }), [responseData]);

  const tipoDocumentoOptions = [
    { value: 39, label: "Boleta Electrónica" },
    { value: 41, label: "Boleta Exenta Electrónica" },
  ];

  const paginationOpciones = {
    rowsPerPageText: "Filas por página",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsText: "Todos",
  };

  return (
    <ContainerRangosFolios
      empresaOptions={empresaOptions}
      companyLoading={companyLoading}
      tipoDocumentoOptions={tipoDocumentoOptions}
      deviceOptions={deviceOptions}
      deviceLoading={deviceLoading}
      empresa={empresa}
      setEmpresa={setEmpresa}
      tipoDocumento={tipoDocumento}
      setTipoDocumento={setTipoDocumento}
      device={device}
      setDevice={setDevice}
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
      folioIni={folioIni}
      setFolioIni={setFolioIni}
      folioFin={folioFin}
      setFolioFin={setFolioFin}
      handleSearch={handleSearch}
      searching={searching}
      searchError={searchError}
      responseData={responseData}
      columns={columns}
      paginationOpciones={paginationOpciones}
      actionsMemo={actionsMemo}
    />
  );
}

export default RangosFolios;
